import React, { useMemo, useState, useEffect, useRef} from 'react';
import GridSearchTemplate from "../GridSearchTemplate";
import { Products } from '../../api/services/next';
import { Form, Icon, Modal, Popup, Button } from 'semantic-ui-react';
import { useTextResources } from '../../language';
import DropDown from '../../components/shared/dropdown';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import { DatePicker } from '../../components/shared/DatePicker';
import RatesDropDown from '../../components/shared/RatesDropDown';
import RoomTypesDropDown from '../../components/shared/RoomTypesDropDown';
import CustomerDropDown from '../../components/shared/CustomerDropDown';
import { currencyFormat } from '../../components/shared/utils';
import formatCurrency from 'format-currency';
import CheckboxWrapper from '../../components/shared/Checkbox';
import GridPanel from '../../components/GridPanel';
import { dateFormat } from '../../components/shared/constants';
import moment from 'moment';
import ReactJson from '@microlink/react-json-view';
import JsonViewerModal from '../../components/shared/JsonViewerModal';
import { PaxInput } from '../../components/PaxInput';
import PromotionsDropDown from '../../components/shared/PromotionsDropDown';
import { onWarning } from '../../components/shared/messages';
import TextInput from "../../components/shared/TextInput";
import "./style.css";

const ProductSearch = () => {
  const text = useTextResources()

  const [hotelId, setHotelId] = useState(window.user?.CurrentHotel.Id);
  const [datesRange, setDatesRange] = useState();
  const [includePartiallyPriced, setIncludePartiallyPriced] = useState(false);

  // Simulation mode value
  const [simulateMode, setSimulateMode] = useState('');

  // Manual (base) state values
  const [restrictions, setRestrictions] = useState(false);
  const [rateAvailabilitySettings, setRateAvailabilitySettings] = useState(false);
  const [lastRoomValue, setLastRoomValue] = useState(false);
  const [roomAvailability, setRoomAvailability] = useState(false);
  const [allocation, setAllocation] = useState(false);
  const [availableInPms, setAvailableInPms] = useState(false);
  const [availabilityIncludeOb, setAvailabilityIncludeOb] = useState(false);
  const changeField = useRef();

  // Mode helpers
  const isManual = !simulateMode || simulateMode === 'Manual';
  const isPms = simulateMode === 'PMS';
  const isG5Mode = simulateMode === 'SimulateG5' || simulateMode === 'G5Only' || simulateMode === 'IncludeG5Simulation';

   useEffect(() => {
    if (isManual) {
        setRestrictions(false);
        setRateAvailabilitySettings(false);
        setLastRoomValue(false);
        setRoomAvailability(false);
        setAllocation(false);
        setAvailabilityIncludeOb(false);
        setAvailableInPms(false);
        if (changeField.current) {
            changeField.current('IncludeRateAvailabilitySettings', false);
            changeField.current('ConsiderAvailability', false);
            changeField.current('ConsiderAllocations', false);
            changeField.current('ConsiderOverbooking', false);
            changeField.current('ConsiderAvailableInPMS', false);
        }
    } else {
      if (changeField.current) {
            changeField.current('IgnoreRestrictions', false);
            changeField.current('IncludeRateAvailabilitySettings', true);
            changeField.current('IgnoreLRV', false);
            changeField.current('ConsiderAvailability', true);
            changeField.current('ConsiderAllocations', true);
            changeField.current('ConsiderOverbooking', true);
            if (isG5Mode) {
                changeField.current('PromotionId', null);
                changeField.current('ConsiderAvailableInPMS', false);
            }
            else if (isPms){
                changeField.current('ConsiderAvailableInPMS', true);
            }
        }
    } 
        
    if (!isG5Mode) {
      if (changeField.current) {
          changeField.current('G5Filter.GuestID', null);
          changeField.current('G5Filter.ReasonCode', null);
          changeField.current('G5Filter.ClubCode', null);
          changeField.current('G5Filter.PromoCode', null);
      }
    }
  }, [simulateMode]);

    const getDatesRangeFilter = () => {
        return (
            datesRange && [
                moment(datesRange[0]).toISOString(),
                moment(datesRange[1]).toISOString()
            ]
        );
    }

    const isDayUse = () => {
        return datesRange && moment(datesRange[0]).format('YYYY-MM-DD') == moment(datesRange[1]).format('YYYY-MM-DD');

    }
    const productSearchIncludes = useMemo(() => {
        const includes = ["Rate",
            "ProductPrices",
            "DailyProductPrices",
            "DailyProductPricesLogs",
            "Restrictions",
            "RatePrices",
            "Commissions",
            "ReservationPolicyId"];
   
        includePartiallyPriced && includes.push("PartiallyPriced");

    return includes;
  }, [includePartiallyPriced]);

    const byHotelSpec = useMemo(
        () => ({ HotelId: hotelId }),
        [hotelId]
    );
    const currencyFormatter = ({ value, row }) => {
        return formatCurrency(value || 0, currencyFormat(row?.Rate?.CurrencyId));
    }
    const dateFormatter = ({ value }) => {
        return value && moment(value).format(dateFormat);
    };

    const detailsFormatter = ({ row }) => {
        if(row.G5Request != null)
            return <div>
                <JsonViewerModal data={JSON.parse(row.G5Request)} header="G5 Request JSON" icon="file alt" />
                <JsonViewerModal data={JSON.parse(row.G5Response)} header="G5 Response JSON" icon="file alt" /></div>
        else if(!row.IsG5Product)
            return <div><DailyPriceModal product={row} />
                <JsonViewerModal data={row} header="PMS Product JSON" /></div>
    }

    const logFormatter = ({ row }) => {
        return <div><CalculationLogModal log={row.PricingCalculationLog} /></div>
    }
    const rateFormatter = ({ value }) => {
        if (value) {
            return <Popup
                on='click'
                trigger={<a style={{ cursor: "pointer" }}>{value.Name}</a>}
                content={<ReactJson src={value} style={{ width: "max-content", height: 400, overflow: "auto" }}></ReactJson>}
            />
        } else {
            return "";
        }
    }

    const CalculationLogModal = ({ log }) => {
        return <Modal
            trigger={<Icon link size='large' name="file text"/>}
            header={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px' }}>
                <span>Calculation Log</span>
                <Button icon='copy' size='tiny' title='Copy JSON' onClick={() => navigator.clipboard.writeText(JSON.stringify(log, null, 2))} />
              </div>
            }
            content={
                <Modal.Content>
                    <ReactJson src={log} style={{ width: "max-content", height: 400, overflow: "auto" }}></ReactJson>
                </Modal.Content>
            }
        />
    }

  const DailyPriceModal = ({ product }) => {
    const options = {
      columns: [
        { name: "Date", title: text("Date"), formatter: dateFormatter },
        { name: "Description", title: text("Description") },
        { name: 'PriceBeforeTax', title: text('PriceBeforeTax'), formatter: currencyFormatter },
        { name: 'PriceAfterTax', title: text('PriceAfterTax'), formatter: currencyFormatter },
        { name: 'Log', title: text('Log'), formatter: logFormatter }
      ],
        customOptions: {
            group: {
                grouping: [
                    { columnName: 'Date' }
                ]
            }
        },
        menu: product.PromotionId ?
        [
        <div
          key={product.RateDiscountAssignmentId}
          style={{marginInlineEnd:"50px", width:"230px"}}
        >
          {`Promotion Id: ${product.RateDiscountAssignmentId}`}
        </div>
        ] :
        [],
      summary: true,
      groupSummaryItems: [
        { columnName: 'PriceBeforeTax', type: 'sum', showInGroupFooter: false, alignByColumn: true, formatter: currencyFormatter },
        { columnName: 'PriceAfterTax', type: 'sum', showInGroupFooter: false, alignByColumn: true, formatter: currencyFormatter }
        ],
    }
    return <Modal
      trigger={<Icon link size='large' name="calendar alternate outline" />}
      header="Daily prices"
      content={
        <Modal.Content style={{ maxHeight: 600, display: 'flex' }}>
          <GridPanel data={product.DailyPrices.filter(x => x.EntityTypeId !== 0)} options={options} />
        </Modal.Content>
      }
      />
  }

  const isValidBeforeSearch = (state) => {
    const missing = [];
    if (!state.HotelId) missing.push(text("Hotel"));
    if (!state.CustomerId) missing.push(text("Customer"));
    if (!state["Pax.Adults"] && !state["Pax.Children"] && !state["Pax.Infants"]) missing.push(text("Pax"));
    if (!state.FromToDates) missing.push(text("Date"));
    if (missing.length) {
      onWarning(text("MissingFields").replace('{0}', missing.join(", ")));
      return false;
    }
    return true;
  }

  // G5 extra fields rendered when G5 family active
  const g5ExtraFields = simulateMode === 'IncludeG5Simulation' || simulateMode === 'SimulateG5' ? [
    <Form.Field
      control={TextInput}
      label={text("Club")}
      key='G5Filter.ClubCode'
    />,
    <Form.Field
      control={TextInput}
      label={text("GuestID")}
      key='G5Filter.GuestID'
    />,
    <Form.Field
      control={TextInput}
      label={text("ReasonCodes")}
      key='G5Filter.ReasonCode'
    />,
    <Form.Field
      control={TextInput}
      label={text("PromoCode")}
      key='G5Filter.PromoCode'
    />
  ] : [];

  const advancedFields = [
    <Form.Field
      key='IncludePartiallyPriced'
      control={CheckboxWrapper}
      label={text("IncludePartiallyPriced")}
      onChange={setIncludePartiallyPriced}
    />,
    <Form.Field
      control={CheckboxWrapper}
      label={text("HomeSiteDiscount")}
      key='ConsiderHomeSiteDiscount'
    />,
    ...g5ExtraFields
  ];


  const gridOptions = {
    search: {
      fields: [
        <Form.Field
          key='HotelId'
          label={text('Hotel')}
          control={HotelsDropDown}
          onChange={setHotelId}
          placeholder={text('SelectHotel')}
          clearable
        />,
        <DatePicker
          picktype='range'
          label={text('Dates')}
          key='FromToDates'
          onChange={setDatesRange}
          clearable
        />,
        <Form.Field
          label={text("RoomType")}
          placeholder={text("Select")}
          control={RoomTypesDropDown}
          search
          spec={byHotelSpec}
          key='RoomTypeId'
          clearable
        />,
        <Form.Field
          label={text("Customer")}
          placeholder={text("Select")}
          search
          control={CustomerDropDown}
          spec={byHotelSpec}
          key='CustomerId'
          clearable
        />,
        <Form.Field
          label={text("Rate")}
          placeholder={text("Select")}
          spec={byHotelSpec}
          search
          control={RatesDropDown}
          key='RateId'
          clearable
        />,
        <Form.Field
          label={text("Pax")}
          control={PaxInput}
          key='Pax'
        />,
        <Form.Field
          label={text("Promotion")}
          control={PromotionsDropDown}
          spec={byHotelSpec}
          search
          clearable
          key='PromotionId'
          disabled={isG5Mode}
        />,
        <Form.Field key='Empty1'/>,
        <Form.Field
          key='SimulateMode'
          label={text('SimulateMode')}
          control={DropDown}
          data={[
            { text: text('Manual'), value: 'Manual' },
            { text: text('PMS'), value: 'PMS' },
            { text: text('G5Only'), value: 'G5Only' },
            { text: text('IncludeG5Simulation'), value: 'IncludeG5Simulation' },
            { text: text('SimulateG5'), value: 'SimulateG5' }
          ]}
          valueField="value"
          textField="text"
          selectFirst={true}
          onChange={value => setSimulateMode(value)}
        />,
        <Form.Field key='Empty2'/>,
        <Form.Field key='Empty3'/>,
        <Form.Field key='Empty4'/>,
        <Form.Field key='Empty5'/>,
        <Form.Field key='Empty6'/>,
        <Form.Field key='Empty7'/>,
        <Form.Field key='Empty8'/>,
        <Form.Field label={`${text('Consider')}:`}
          className= {"Consider"}/>,
        <Form.Field
          key='IgnoreRestrictions'
          control={CheckboxWrapper}
          label={text("Restrictions")}
          checked={isManual ? restrictions : true}
          onChange={val => setRestrictions(val)}
          className={!isManual ? "disabled-checkbox" : ""}
        />,
        <Form.Field
          key='IncludeRateAvailabilitySettings'
          control={CheckboxWrapper}
          label={text("RateAvailabilitySettings")}
          checked={isManual ? rateAvailabilitySettings : true}
          onChange={val => setRateAvailabilitySettings(val)}
          className={!isManual ? "disabled-checkbox" : ""}
        />,
        <Form.Field
          key='IgnoreLRV'
          control={CheckboxWrapper}
          label={text("LastRoomValue")}
          checked={isManual ? lastRoomValue : true}
          onChange={val => setLastRoomValue(val)}
          className={!isManual ? "disabled-checkbox" : ""}
        />,
        <Form.Field
          key='ConsiderAvailability'
          control={CheckboxWrapper}
          label={text("RoomAvailability")}
          checked={isManual ? roomAvailability : true}
          onChange={val => setRoomAvailability(val)}
          className={!isManual ? "disabled-checkbox" : ""}
        />,
        <Form.Field
          key='ConsiderAllocations'
          control={CheckboxWrapper}
          label={text("Allocation")}
          checked={isManual ? allocation : true}
          onChange={val => setAllocation(val)}
          className={!isManual ? "disabled-checkbox" : ""}
        />,
        <Form.Field
          key='ConsiderOverbooking'
          control={CheckboxWrapper}
          label={text("AvailabilityIncludingOverbooking")}
          checked={isManual ? availabilityIncludeOb : true}
          onChange={val => setAvailabilityIncludeOb(val)}
          className={!isManual ? "disabled-checkbox" : ""}
        />,
        <Form.Field
          key='ConsiderAvailableInPMS'
          control={CheckboxWrapper}
          label={text("AvailabilityPms")}
          checked={isManual ? availableInPms : (isPms ? true : false)}
          onChange={val => setAvailableInPms(val)}
          className={!isManual ? "disabled-checkbox" : ""}
        />
      ],
      advancedFields,
      method: 'GET',
      api: Products.Search,
          spec: {
              FromToDates: getDatesRangeFilter(),
              includes: productSearchIncludes,
              GetPricingForLastDateOfRange: isDayUse(),
              ConsiderCustomerDiscount: true,
              IncludeBaseProductsWithPromotions: true,
              IncludePromotions: true,
              OnlyG5: simulateMode === 'SimulateG5',
              IncludeG5: simulateMode === 'IncludeG5Simulation',
              IgnoreRestrictions: isManual ? !restrictions : false,
              IgnoreLRV: isManual ? !lastRoomValue : false,
              TrimNonApplicableRestrictionFields: true
          },
      
      setChangeFieldRef: ref=> changeField.current = ref,
      validate: (state) => {
        if (isG5Mode) {
            return isValidBeforeSearch(state);
        }
        return true;
      },
    },
    grid: {
      columns: [
        { name: 'Name', title: text('Name') },
        { name: 'Rate', title: text('Rate'), formatter: rateFormatter },
        { name: 'TotalPriceBeforeTax', title: text('PriceBeforeTax'), formatter: currencyFormatter },
        { name: 'TotalPriceAfterTax', title: text('PriceAfterTax'), formatter: currencyFormatter },
        { name: 'Details', title: text('Details'), formatter: detailsFormatter },
      ],
          rowClassCallback: a => a.IsG5Product ? "G5Product" : ""
        },
    }
    return <GridSearchTemplate options={gridOptions} />;
}

ProductSearch.propTypes = {

};
export default ProductSearch;
